import { useMemo, useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  BASE_CURRENCY,
  MAINTENANCE_KINDS,
  MAINTENANCE_KIND_LABELS,
  RETIRE_REASONS,
  RETIRE_REASON_LABELS,
  validateAssignInput,
  validateMaintenanceCloseInput,
  validateMaintenanceOpenInput,
  validateRetireInput,
  type AssignInput,
  type MaintenanceCloseInput,
  type MaintenanceOpenInput,
  type RetireInput,
} from "@torre/assets";

import Modal from "../../../components/base/Modal";
import { Input, Select, Textarea } from "../../../components/ui";
import { useActivosMutation, useBases, usePeople, useSuppliers, useVehicles } from "../hooks/useActivos";
import { handleSaveError } from "../lib/conflicts";
import { fieldErrors, optionalText, parseAmount, todayIso } from "../lib/format";
import { langOf, optionsOf } from "../lib/vocab";
import { activosApi, type AssetRow, type MaintenanceRow } from "../services/activosApi";
import { FormBlock, FormFooter } from "./shared";

// ============================================================================
// Acciones sobre un activo: asignar, mantenimiento (abrir y cerrar) y baja.
// Archivo común, idéntico en CC-CONSOLE y WAYAK. Cada una valida con
// `@torre/assets` antes de mandar; el API decide la transición de estado y deja
// el evento en la línea de tiempo.
// ============================================================================

const invoiceOf = (uuid: string, serie: string, folio: string) =>
  uuid.trim() || serie.trim() || folio.trim() ? { uuid: optionalText(uuid), serie: optionalText(serie), folio: optionalText(folio) } : null;

function useSaveFlow() {
  const { t, i18n } = useTranslation("activos");
  const lang = langOf(i18n.language);
  const navigate = useNavigate();
  const [errors, setErrors] = useState<Record<string, string>>({});
  const run = async (save: () => Promise<unknown>, okKey: string, onDone: () => void) => {
    try {
      await save();
      toast.success(t(okKey));
      onDone();
    } catch (err) {
      const outcome = await handleSaveError(err, t, { navigate });
      if (outcome.kind === "fields") setErrors(fieldErrors(outcome.errors, lang));
      if (outcome.kind === "redirected") onDone();
    }
  };
  return { t, lang, errors, setErrors, run };
}

// --- Asignar ---------------------------------------------------------------------------------

export function AssignModal({ asset, onClose }: { asset: AssetRow; onClose: () => void }) {
  const { t, lang, errors, setErrors, run } = useSaveFlow();
  const people = usePeople();
  const vehicles = useVehicles();
  const bases = useBases();
  const a = asset.assignment;
  const [custodianId, setCustodianId] = useState(a?.custodian?.id || "");
  const [vehicleId, setVehicleId] = useState(a?.vehicleId || "");
  const [baseId, setBaseId] = useState(a?.baseId || "");
  const [location, setLocation] = useState(a?.location || "");
  const save = useActivosMutation((input: AssignInput) => activosApi.assign(asset.id, input));

  const none = { value: "", label: t("common.none") };
  const personOptions = useMemo(() => (people.data || []).map((p) => ({ value: p.id, label: p.email ? `${p.name} · ${p.email}` : p.name })), [people.data]);
  const vehicleOptions = useMemo(() => (vehicles.data || []).map((v) => ({ value: v.id, label: v.name })), [vehicles.data]);
  const baseOptions = useMemo(() => (bases.data || []).map((b) => ({ value: b.id, label: b.name })), [bases.data]);

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    const payload: AssignInput = {
      // El API vuelve a leer a la persona y guarda su nombre y correo.
      custodian: custodianId ? { type: "user", id: custodianId, name: null, email: null, company_id: null } : null,
      vehicleId: vehicleId || null,
      baseId: baseId || null,
      location: optionalText(location),
    };
    const checked = validateAssignInput(payload);
    if (!checked.ok) {
      setErrors({ ...fieldErrors(checked.errors, lang), ...(checked.errors.some((x) => x.field === "") ? { _: t("assign.needOne") } : {}) });
      return;
    }
    setErrors({});
    void run(() => save.mutateAsync(payload), "assign.saved", onClose);
  };

  return (
    <Modal
      title={t("assign.title", { name: asset.name })}
      onClose={onClose}
      width="w-full max-w-xl"
      content={
        <form onSubmit={onSubmit} noValidate data-testid="assign-form">
          <p className="mb-3 text-xs text-content-muted">{t("assign.help")}</p>
          <div className="space-y-3">
            <Select label={t("fields.custodian")} value={custodianId} onChange={(e) => setCustodianId(e.target.value)} options={[none, ...personOptions]} error={errors["custodian.id"]} />
            <Select label={t("fields.vehicle")} value={vehicleId} onChange={(e) => setVehicleId(e.target.value)} options={[none, ...vehicleOptions]} error={errors.vehicleId} />
            <Select label={t("fields.base")} value={baseId} onChange={(e) => setBaseId(e.target.value)} options={[none, ...baseOptions]} error={errors.baseId} />
            <Input label={t("fields.location")} value={location} onChange={(e) => setLocation(e.target.value)} error={errors.location} />
            {errors._ ? <p className="text-xs text-error" data-testid="assign-error">{errors._}</p> : null}
          </div>
          <FormFooter onCancel={onClose} submitting={save.isPending} submitLabel={t("assign.submit")} submitTestId="assign-submit" />
        </form>
      }
    />
  );
}

// --- Mantenimiento ---------------------------------------------------------------------------

interface MoneyState {
  cost: string;
  currency: string;
  exchangeRate: string;
  uuid: string;
  serie: string;
  folio: string;
}

function MoneyFields({ state, onChange, errors, canSeeCosts }: { state: MoneyState; onChange: (k: keyof MoneyState, v: string) => void; errors: Record<string, string>; canSeeCosts: boolean }) {
  const { t } = useTranslation("activos");
  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
      {canSeeCosts ? (
        <>
          <Input label={t("fields.cost")} inputMode="decimal" value={state.cost} onChange={(e) => onChange("cost", e.target.value)} error={errors.cost} />
          <Input label={t("fields.currency")} value={state.currency} maxLength={3} onChange={(e) => onChange("currency", e.target.value)} error={errors.currency} />
          <Input label={t("fields.exchangeRate")} inputMode="decimal" value={state.exchangeRate} onChange={(e) => onChange("exchangeRate", e.target.value)} error={errors.exchangeRate} />
        </>
      ) : null}
      <Input label={t("fields.invoiceUuid")} value={state.uuid} onChange={(e) => onChange("uuid", e.target.value)} error={errors["invoice.uuid"]} containerClassName={canSeeCosts ? undefined : "sm:col-span-2"} />
      <Input label={t("fields.invoiceSerie")} value={state.serie} onChange={(e) => onChange("serie", e.target.value)} error={errors["invoice.serie"]} />
      <Input label={t("fields.invoiceFolio")} value={state.folio} onChange={(e) => onChange("folio", e.target.value)} error={errors["invoice.folio"]} />
    </div>
  );
}

const emptyMoney = (m?: MaintenanceRow | null): MoneyState => ({
  cost: m?.cost != null ? String(m.cost) : "",
  currency: m?.currency || BASE_CURRENCY,
  exchangeRate: m?.exchangeRate != null ? String(m.exchangeRate) : "",
  uuid: m?.invoice?.uuid || "",
  serie: m?.invoice?.serie || "",
  folio: m?.invoice?.folio || "",
});

export function OpenMaintenanceModal({ asset, canSeeCosts, onClose }: { asset: AssetRow; canSeeCosts: boolean; onClose: () => void }) {
  const { t, lang, errors, setErrors, run } = useSaveFlow();
  const suppliers = useSuppliers({});
  const [kind, setKind] = useState<string>("correctivo");
  const [openedAt, setOpenedAt] = useState(todayIso());
  const [supplierId, setSupplierId] = useState("");
  const [description, setDescription] = useState("");
  const [nextDueAt, setNextDueAt] = useState(asset.maintenance?.nextDueAt || "");
  const [money, setMoney] = useState<MoneyState>(emptyMoney());
  const save = useActivosMutation((input: MaintenanceOpenInput) => activosApi.openMaintenance(asset.id, input));

  const supplierOptions = useMemo(
    () => (suppliers.data?.items || []).map((s) => ({ value: s.id, label: s.roles?.includes("repara") ? `${s.name} · ${t("maintenance.repairs")}` : s.name })),
    [suppliers.data, t],
  );

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    const payload: MaintenanceOpenInput = {
      kind: kind as MaintenanceOpenInput["kind"],
      openedAt: optionalText(openedAt),
      supplierId: supplierId || null,
      description: optionalText(description),
      invoice: invoiceOf(money.uuid, money.serie, money.folio),
      nextDueAt: optionalText(nextDueAt),
      ...(canSeeCosts
        ? { cost: parseAmount(money.cost), currency: optionalText(money.currency) || BASE_CURRENCY, exchangeRate: parseAmount(money.exchangeRate) }
        : {}),
    };
    const checked = validateMaintenanceOpenInput(payload, { canSeeCosts });
    if (!checked.ok) {
      setErrors(fieldErrors(checked.errors, lang));
      return;
    }
    setErrors({});
    void run(() => save.mutateAsync(payload), "maintenance.opened", onClose);
  };

  return (
    <Modal
      title={t("maintenance.openTitle", { name: asset.name })}
      onClose={onClose}
      width="w-full max-w-3xl"
      content={
        <form onSubmit={onSubmit} noValidate data-testid="maintenance-open-form">
          <FormBlock title={t("maintenance.blocks.service")}>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Select label={t("fields.maintenanceKind")} required value={kind} onChange={(e) => setKind(e.target.value)} options={optionsOf(MAINTENANCE_KINDS, MAINTENANCE_KIND_LABELS, lang)} error={errors.kind} />
              <Input label={t("fields.openedAt")} type="date" value={openedAt} onChange={(e) => setOpenedAt(e.target.value)} error={errors.openedAt} />
              <Select label={t("fields.workshop")} value={supplierId} onChange={(e) => setSupplierId(e.target.value)} options={[{ value: "", label: t("common.none") }, ...supplierOptions]} error={errors.supplierId} />
              <Input label={t("fields.nextMaintenance")} type="date" value={nextDueAt} onChange={(e) => setNextDueAt(e.target.value)} error={errors.nextDueAt} />
            </div>
            <Textarea label={t("fields.description")} rows={3} value={description} onChange={(e) => setDescription(e.target.value)} error={errors.description} />
          </FormBlock>
          <FormBlock title={t("maintenance.blocks.invoice")}>
            <MoneyFields state={money} onChange={(k, v) => setMoney((p) => ({ ...p, [k]: v }))} errors={errors} canSeeCosts={canSeeCosts} />
          </FormBlock>
          <p className="mt-3 text-xs text-content-muted">{t("maintenance.openHelp")}</p>
          <FormFooter onCancel={onClose} submitting={save.isPending} submitLabel={t("maintenance.openSubmit")} submitTestId="maintenance-open-submit" />
        </form>
      }
    />
  );
}

export function CloseMaintenanceModal({ maintenance, canSeeCosts, onClose }: { maintenance: MaintenanceRow; canSeeCosts: boolean; onClose: () => void }) {
  const { t, lang, errors, setErrors, run } = useSaveFlow();
  const [closedAt, setClosedAt] = useState(todayIso());
  const [description, setDescription] = useState(maintenance.description || "");
  const [nextDueAt, setNextDueAt] = useState("");
  const [money, setMoney] = useState<MoneyState>(emptyMoney(maintenance));
  const save = useActivosMutation((input: MaintenanceCloseInput) => activosApi.closeMaintenance(maintenance.id, input));

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    const payload: MaintenanceCloseInput = {
      closedAt: optionalText(closedAt),
      description: optionalText(description),
      invoice: invoiceOf(money.uuid, money.serie, money.folio),
      ...(nextDueAt ? { nextDueAt } : {}),
      ...(canSeeCosts
        ? { cost: parseAmount(money.cost), currency: optionalText(money.currency) || BASE_CURRENCY, exchangeRate: parseAmount(money.exchangeRate) }
        : {}),
    };
    const checked = validateMaintenanceCloseInput(payload, { canSeeCosts, openedAt: maintenance.openedAt });
    if (!checked.ok) {
      setErrors(fieldErrors(checked.errors, lang));
      return;
    }
    setErrors({});
    void run(() => save.mutateAsync(payload), "maintenance.closed", onClose);
  };

  return (
    <Modal
      title={t("maintenance.closeTitle")}
      onClose={onClose}
      width="w-full max-w-3xl"
      content={
        <form onSubmit={onSubmit} noValidate data-testid="maintenance-close-form">
          <FormBlock title={t("maintenance.blocks.service")}>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Input label={t("fields.closedAt")} type="date" value={closedAt} onChange={(e) => setClosedAt(e.target.value)} error={errors.closedAt} />
              <Input label={t("fields.nextMaintenance")} type="date" value={nextDueAt} onChange={(e) => setNextDueAt(e.target.value)} error={errors.nextDueAt} />
            </div>
            <Textarea label={t("fields.description")} rows={3} value={description} onChange={(e) => setDescription(e.target.value)} error={errors.description} />
          </FormBlock>
          <FormBlock title={t("maintenance.blocks.invoice")}>
            <MoneyFields state={money} onChange={(k, v) => setMoney((p) => ({ ...p, [k]: v }))} errors={errors} canSeeCosts={canSeeCosts} />
          </FormBlock>
          <p className="mt-3 text-xs text-content-muted">{t("maintenance.closeHelp")}</p>
          <FormFooter onCancel={onClose} submitting={save.isPending} submitLabel={t("maintenance.closeSubmit")} submitTestId="maintenance-close-submit" />
        </form>
      }
    />
  );
}

// --- Baja (no borra) -------------------------------------------------------------------------

export function RetireModal({ asset, onClose }: { asset: AssetRow; onClose: () => void }) {
  const { t, lang, errors, setErrors, run } = useSaveFlow();
  const [reason, setReason] = useState("");
  const [date, setDate] = useState(todayIso());
  const [note, setNote] = useState("");
  const save = useActivosMutation((input: RetireInput) => activosApi.retire(asset.id, input));

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    const payload = { reason, date: optionalText(date), note: optionalText(note) };
    const checked = validateRetireInput(payload);
    if (!checked.ok) {
      setErrors(fieldErrors(checked.errors, lang));
      return;
    }
    setErrors({});
    void run(() => save.mutateAsync(checked.value), "retire.saved", onClose);
  };

  return (
    <Modal
      title={t("retire.title", { name: asset.name })}
      onClose={onClose}
      width="w-full max-w-md"
      content={
        <form onSubmit={onSubmit} noValidate data-testid="retire-form">
          <p className="mb-3 text-xs text-content-muted">{t("retire.help")}</p>
          <div className="space-y-3">
            <Select
              label={t("fields.retireReason")}
              required
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              options={optionsOf(RETIRE_REASONS, RETIRE_REASON_LABELS, lang)}
              placeholder={t("common.choose")}
              error={errors.reason}
            />
            <Input label={t("fields.retiredAt")} type="date" value={date} onChange={(e) => setDate(e.target.value)} error={errors.date} />
            <Textarea label={t("fields.note")} rows={3} value={note} onChange={(e) => setNote(e.target.value)} error={errors.note} />
          </div>
          <FormFooter onCancel={onClose} submitting={save.isPending} submitLabel={t("retire.submit")} submitTestId="retire-submit" />
        </form>
      }
    />
  );
}
